import { ChevronDown, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { selectRegions, selectRegionsLoading, useAppSelector } from '@/store'
import ScrollButton, { ScrollPosition } from './ScrollButton'
import TreeNode from './TreeNode'

interface SingleBranchSelectProps {
  selectedBranch: string | undefined
  onSelectBranch: (branchName: string) => void
}

export const SingleBranchSelect = ({
  selectedBranch,
  onSelectBranch,
}: SingleBranchSelectProps) => {
  const REGIONS = useAppSelector(selectRegions)
  const regionsLoading = useAppSelector(selectRegionsLoading)

  // Filter out "Other" regions if necessary, similar to BasicBranchSelect
  // For now, consistent with typical usage
  const selectableRegions = useMemo(() => {
    return REGIONS.filter((region) => region.name !== 'Other')
  }, [REGIONS])

  const [expandedRegions, setExpandedRegions] = useState<Set<number>>(new Set())
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
    isAtTop: true,
    isAtBottom: false,
  })
  const [isOpen, setIsOpen] = useState(false)

  // Auto-expand the region containing the currently selected branch
  useEffect(() => {
    if (!selectedBranch || selectableRegions.length === 0) return

    const region = selectableRegions.find((r) =>
      r.branches.some((b) => b.name === selectedBranch)
    )

    if (region && !expandedRegions.has(region.id)) {
      setExpandedRegions((prev) => new Set([...prev, region.id]))
    }
  }, [selectedBranch, selectableRegions])

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const isAtTop = scrollTop === 0
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1
    setScrollPosition({ isAtTop, isAtBottom })
  }, [])

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
    if (open) {
      setScrollPosition({ isAtTop: true, isAtBottom: false })
      const container = document.querySelector('.picker-content')
      if (container) container.scrollTop = 0
    }
  }, [])

  const toggleRegionExpansion = useCallback((regionId: number) => {
    setExpandedRegions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(regionId)) newSet.delete(regionId)
      else newSet.add(regionId)
      return newSet
    })
  }, [])

  const handleBranchClick = useCallback(
    (branchName: string) => {
      onSelectBranch(branchName)
      setIsOpen(false) // Close popover on selection
    },
    [onSelectBranch]
  )

  if (regionsLoading || REGIONS.length === 0) {
    return (
      <Button
        variant="outline"
        className="justify-start h-8 font-normal text-black w-[200px]"
        disabled
      >
        <span className="truncate">Loading...</span>
        <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
      </Button>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="justify-start h-8 font-normal w-[200px]"
        >
          <span className="truncate">{selectedBranch || 'Select Branch'}</span>
          <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[400px] p-0">
        <div className="h-[350px] flex flex-col">
          {!scrollPosition.isAtTop && (
            <ScrollButton
              direction="up"
              onClick={() => {
                const container = document.querySelector('.picker-content')
                if (container) container.scrollTop -= 100
              }}
            />
          )}

          <div
            className="flex-1 p-2 overflow-y-auto picker-content [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onScroll={handleScroll}
          >
            {selectableRegions.map((region) => {
              const isExpanded = expandedRegions.has(region.id)
              // Check if region contains the selected branch (for highlighting/checking region if we wanted)
              // In single select, usually we just check the branch itself.

              return (
                <div key={region.id} className="mb-1">
                  {/* Region Node - Not selectable itself in single branch mode, just expands */}
                  <TreeNode
                    icon={
                      isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )
                    }
                    label={region.name}
                    checked={false} // Region itself isn't "checked" in single branch mode
                    onCheck={() => {}} // No-op for checkbox
                    onClick={() => toggleRegionExpansion(region.id)}
                    className="text-sm"
                    type="date"
                    branchOnly={true} // Hides checkbox for region if branchOnly logic is reused or simple mode
                    // Note: TreeNode might need adjustment if we want to hide checkbox completely
                    // or we can just ignore 'checked' visual if we customize TreeNode or pass specific props
                  />

                  {isExpanded && (
                    <div className="ml-10 mt-1">
                      {region.branches.map((branch) => (
                        <TreeNode
                          key={branch.id}
                          icon={null}
                          label={branch.name}
                          checked={branch.name === selectedBranch}
                          onCheck={() => handleBranchClick(branch.name)}
                          onClick={() => handleBranchClick(branch.name)}
                          className="text-sm"
                          type="date"
                          // branchOnly={true} // Ensure checkboxes behave as radios or just selection indicators
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {!scrollPosition.isAtBottom && (
            <ScrollButton
              direction="down"
              onClick={() => {
                const container = document.querySelector('.picker-content')
                if (container) container.scrollTop += 100
              }}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
